import { ReactNode } from "react";
import { Link as RouterLink } from "react-router-dom";
import { ArrowForwardIos } from "@mui/icons-material";
import { Box, Card, Link, ListItem, Skeleton, Stack, styled, Typography as T } from "@mui/material";
import useDataset from "datasets/hooks/useDataset";
import { getAccentColor } from "datasets/utils/color";

const StyledCard = styled(Card)(() => ({
  borderRadius: 10,
  boxShadow: "none",
  overflow: "visible",
  position: "relative",
  transition: "all 0.2s cubic-bezier(0.165, 0.84, 0.44, 1)",
  zIndex: 1,
  "&:hover": {
    transform: "scale(1.02)",
  },
  "&:after": {
    borderRadius: "inherit",
    boxShadow: "0px 1px 4px 0px #D6D6D8",
    content: "''",
    height: "100%",
    left: 0,
    position: "absolute",
    right: 0,
    top: 0,
    transition: "all 0.2s cubic-bezier(0.165, 0.84, 0.44, 1)",
    zIndex: -1,
  },
  "&:hover:after": {
    boxShadow: "0px 1px 1px 0px #D6D6D885, 0px 1px 10px 0px #D6D6D8",
  },
}));

const NavigationCard = ({
  title,
  icon,
  description,
  url,
  loading,
  isEmpty = false,
  newTab = false,
  state,
}: {
  title: ReactNode;
  icon: ReactNode;
  description: ReactNode;
  loading: boolean;
  url: string;
  isEmpty?: boolean;
  newTab?: boolean;
  state?: any;
}) => {
  const { data: dataset } = useDataset();

  return (
    <ListItem disablePadding>
      <Link
        to={url}
        component={RouterLink}
        target={newTab ? "_blank" : "_self"}
        sx={{ textDecoration: "unset", width: "100%" }}
        state={state}
      >
        <StyledCard>
          <Box display="flex" alignItems="center" gap={3} px={3} py={2.5}>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              bgcolor={!dataset ? "grey.300" : getAccentColor(dataset?.accentColor)}
              borderRadius={56}
              width={56}
              height={56}
            >
              <Box height={28} width={28} color="common.white">
                {icon}
              </Box>
            </Box>
            <Stack flex={1} gap={1}>
              {loading ? (
                <>
                  <Skeleton variant="rounded" height={20} width={120} />
                  <Skeleton variant="text" height={20} width={160} />
                </>
              ) : (
                <>
                  <T
                    fontSize={18}
                    fontWeight={500}
                    lineHeight="19px"
                    color={isEmpty ? getAccentColor(dataset?.accentColor) : "text.primary"}
                  >
                    {title}
                  </T>
                  {!isEmpty && (
                    <T variant="body2" color="#575759" lineHeight={1}>
                      {description}
                    </T>
                  )}
                </>
              )}
            </Stack>
            <ArrowForwardIos sx={{ fontSize: "14px" }} />
          </Box>
        </StyledCard>
      </Link>
    </ListItem>
  );
};

export default NavigationCard;
